import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useLocation } from 'react-router-dom';
import { useQueryParams } from '@marqvision/shared';
import { userProfileAtom } from '~/atoms';
import { FilterQueryParam } from '~/components/Filters/@types';
import { useClientBrandIds } from '~/hooks';
import { MarketplaceListingsRouteEnum, MarketplaceRouteEnum } from '~/router/@types';
import {
  MarqBrandEnumsListingMonitoringStatusEnum,
  MarqCommonEnumsPotentialAuthorizedReasonEnum,
  MarqCommonEnumsScannedStatusEnum,
  MarqCommonEnumsSortedOrderEnum,
  MarqFoundationEnumsCurrencyEnum,
  MonitoringFilterProtocolPagingRequest,
  MonitoringFilterProtocolRequest,
} from '~/swagger/data-contracts';
import { DomainTypeEnum } from '~/types/common/DomainTypeEnum';
import { parseDomainTypeToApiChannelTypeParam, parseValueToValueArray, safeDecodeURIComponent } from '~/utils';
import { clientFilterChipAtom } from '../@atoms';
import { useResetFilterState } from './filter';

export const useGetFilterParams = () => {
  const { brandId } = useClientBrandIds();
  const clientFilterChipState = useAtomValue(clientFilterChipAtom);
  const userProfile = useAtomValue(userProfileAtom);
  const { isFilterApplied: isFilterActivated } = useResetFilterState();
  const { listingStatus, domainType } = usePathParams();
  const { params: queryParam, setParams, removeParams } = useQueryParams<FilterQueryParam>();
  const {
    assets,
    platforms,
    seller_search_keyword,
    report_reason,
    ois,
    smart_rule_ids,
    tm_logo,
    tm_logo_ids,
    tm_text,
    infringing_images_filter,
    likely_counterfeit,
    top_results,
    repeat_offender,
    requested,
    low_price,
    min_price,
    max_price,
    min_sales_volume,
    max_sales_volume,
    start_date,
    end_date,
    search,
    models,
    potential_authorized_reason,
    custom_tag_id_filter,
    ship_from,
    scanned_status_filter,
    include_expired,
    post_type_filter,
    comment_user_type_filter,
    tp_progress_status_filter,
    tp_reported_filter,
  } = queryParam;

  const { sellerNameList: selectedSellerNames } = clientFilterChipState;
  const currency = userProfile?.client_currency || MarqFoundationEnumsCurrencyEnum.KRW;

  const requestFilterParams = useMemo(() => {
    return parseMonitoringFilterProtocolRequest();
  }, [brandId, queryParam, clientFilterChipState]);
  const requestPagingFilterParams = useMemo(() => {
    return parseMonitoringFilterProtocolPagingRequest();
  }, [brandId, queryParam, clientFilterChipState]);
  const legacyRequestFilterParams = useMemo(() => {
    return parseLegacyRequest();
  }, [brandId, queryParam, clientFilterChipState]);

  function parseMonitoringFilterProtocolRequest(): MonitoringFilterProtocolRequest {
    return {
      brand_id: brandId,

      // domain_type: url param -> api param
      channel_type: parseDomainTypeToApiChannelTypeParam(domainType),
      reportable_filter: parseValueToValueArray(report_reason),
      enforcement_rule_id_filter: parseValueToValueArray(smart_rule_ids),
      client_status_filter: listingStatus,
      asset_id_filter: parseValueToValueArray(assets),
      domain_id_filter: parseValueToValueArray(platforms),
      seller_name_filter: parseValueToValueArray(selectedSellerNames),
      search: search ? safeDecodeURIComponent(search) : undefined,
      min_price,
      max_price,
      ...(min_sales_volume && { min_sales_volume: isNaN(min_sales_volume) ? undefined : min_sales_volume }),
      ...(max_sales_volume && { max_sales_volume: isNaN(max_sales_volume) ? undefined : max_sales_volume }),
      currency,

      include_expired: include_expired,

      official_image_id_filter: parseValueToValueArray(ois),
      tm_infringement_filter: tm_logo,
      tm_infringement_logo_filter: parseValueToValueArray(tm_logo_ids),
      tm_infringement_text_filter: tm_text,
      infringing_images_filter,
      potential_authorized_reason_filter: parseValueToValueArray(
        potential_authorized_reason
      ) as MarqCommonEnumsPotentialAuthorizedReasonEnum[],
      gen_ai_counterfeit_filter: likely_counterfeit,
      top_result_filter: top_results,
      repeat_offender_filter: repeat_offender,
      model_id_filter: parseValueToValueArray(models),
      custom_tag_id_filter: parseValueToValueArray(custom_tag_id_filter),
      ship_from_filter: parseValueToValueArray(ship_from),
      client_requested_filter: requested,
      low_price_filter: low_price,
      updated_date_range_filter: {
        start: start_date,
        end: end_date,
      },
      scanned_status_filter: resolveScannedStatusFilter(listingStatus, scanned_status_filter),
      post_type_filter: post_type_filter ?? undefined,
      comment_user_type_filter: parseValueToValueArray(comment_user_type_filter),
      tp_progress_status_filter: parseValueToValueArray(tp_progress_status_filter),
      tp_reported_filter: tp_reported_filter,
    };
  }

  function parseMonitoringFilterProtocolPagingRequest(): MonitoringFilterProtocolPagingRequest {
    return {
      ...requestFilterParams,
      // 프론트에서 필터 sorted_order 를 모두 제거됨
      sorted_order: MarqCommonEnumsSortedOrderEnum.Asc,
      in_search_keyword_filter: seller_search_keyword,
      size: 30,
      search_after: [],
    };
  }

  function parseLegacyRequest() {
    const { channel_type } = requestFilterParams;

    return {
      channel_type,
      asset_id_filter: parseValueToValueArray(assets) || [],
      domain_id_filter: parseValueToValueArray(platforms) || [],
      seller_name_filter: parseValueToValueArray(selectedSellerNames) || [],
      client_status_filter: listingStatus,
    };
  }
  return {
    params: queryParam,
    setParams,
    removeParams,
    isFilterActivated,
    requestFilterParams,
    requestPagingFilterParams,
    legacyRequestFilterParams,
  };
};

const resolveScannedStatusFilter = (
  listingStatus: MarqBrandEnumsListingMonitoringStatusEnum,
  scannedStatusFilter?: MarqCommonEnumsScannedStatusEnum
) => {
  if (listingStatus !== MarqBrandEnumsListingMonitoringStatusEnum.Scanned) return undefined;
  else if (!scannedStatusFilter) return MarqCommonEnumsScannedStatusEnum.All;
  else return scannedStatusFilter;
};

enum PageWithFilterEnum {
  Marketplace = 'marketplace',
  SocialMedia = 'social-media',
  Detection = 'detection',
  Enforcement = 'enforcement',
  TestPurchase = 'testPurchase',
  Archived = 'archived',
  OnHold = 'on-hold',
  ReviewNeeded = 'review-needed',
}

export const usePathParams = () => {
  const { pathname } = useLocation();
  const [_domainType, _section, _tab] = pathname.split('/').filter(Boolean);

  const domainType = _domainType.includes(PageWithFilterEnum.Marketplace)
    ? DomainTypeEnum.Marketplace
    : DomainTypeEnum.SocialMedia;

  let listingStatus: MarqBrandEnumsListingMonitoringStatusEnum = MarqBrandEnumsListingMonitoringStatusEnum.Detection;

  // todo-@jamie: [PROD-13658] social media와 별도 처리가 필요한지 체크
  if (_section === MarketplaceRouteEnum.Listings) {
    switch (_tab) {
      case MarketplaceListingsRouteEnum.Scanned:
        listingStatus = MarqBrandEnumsListingMonitoringStatusEnum.Scanned;
        break;
      case MarketplaceListingsRouteEnum.Flagged:
        listingStatus = MarqBrandEnumsListingMonitoringStatusEnum.Detection;
        break;
      case MarketplaceListingsRouteEnum.Requested:
        listingStatus = MarqBrandEnumsListingMonitoringStatusEnum.Processing;
        break;
      case MarketplaceListingsRouteEnum.ReportSubmitted:
        listingStatus = MarqBrandEnumsListingMonitoringStatusEnum.Reporting;
        break;
      case MarketplaceListingsRouteEnum.SoftNotice:
        listingStatus = MarqBrandEnumsListingMonitoringStatusEnum.SoftNotice;
        break;
      case MarketplaceListingsRouteEnum.OnHold:
        listingStatus = MarqBrandEnumsListingMonitoringStatusEnum.OnHold;
        break;
      case MarketplaceListingsRouteEnum.Removed:
        listingStatus = MarqBrandEnumsListingMonitoringStatusEnum.Removed;
        break;
      case MarketplaceListingsRouteEnum.Denied:
        listingStatus = MarqBrandEnumsListingMonitoringStatusEnum.Denied;
        break;
      case MarketplaceListingsRouteEnum.NotEnforceable:
        listingStatus = MarqBrandEnumsListingMonitoringStatusEnum.NonEnforceable;
        break;
      case MarketplaceListingsRouteEnum.Dismissed:
        listingStatus = MarqBrandEnumsListingMonitoringStatusEnum.Dismissed;
        break;
      case MarketplaceListingsRouteEnum.Archived:
        listingStatus = MarqBrandEnumsListingMonitoringStatusEnum.Archived;
        break;

      // tp
      case MarketplaceListingsRouteEnum.TestPurchase:
        listingStatus = MarqBrandEnumsListingMonitoringStatusEnum.TpAll;
        break;
      default:
        listingStatus = MarqBrandEnumsListingMonitoringStatusEnum.Detection;
        break;
    }
  } else {
    listingStatus = MarqBrandEnumsListingMonitoringStatusEnum.ReviewNeeded;
  }

  return {
    page: _domainType,
    domainType,
    listingStatus,
  };
};

export * from './filter';
